<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">Loading quotation...</div>
            <div v-else>
              <div v-if="readonly" class="alert alert-warning">
                Only draft quotations can be edited. Use status actions from the quotation list for sent or accepted quotations.
              </div>

              <div class="row">
                <div class="col-lg-5 col-sm-12">
                  <div class="form-group">
                    <label>Customer Search</label>
                    <div class="lookup-control-row">
                      <div class="lookup-box">
                        <input
                          v-model="customerSearchQuery"
                          type="text"
                          autocomplete="off"
                          class="form-control"
                          placeholder="Search customer by name or mobile"
                          :disabled="readonly"
                          @focus="showCustomerResults = true"
                          @input="onCustomerLookupInput"
                        />
                        <div v-if="showCustomerResults && !readonly" class="lookup-results">
                          <button
                            v-for="customer in customerSearchResults"
                            :key="customer.customerid"
                            type="button"
                            class="lookup-result-item"
                            @mousedown.prevent="selectCustomer(customer)"
                          >
                            <span class="lookup-result-title">{{ customer.customername || "Unnamed customer" }}</span>
                            <span class="lookup-result-meta">{{ customer.mobile || "No mobile" }}</span>
                          </button>
                          <div v-if="customerSearchQuery.trim() && !customerSearchResults.length" class="lookup-empty-state">
                            No customer found. Use Add Customer.
                          </div>
                        </div>
                      </div>
                      <router-link v-if="!readonly" class="inline-add-btn" to="/addcustomer">
                        <i class="fas fa-user-plus"></i>
                        <span>Add Customer</span>
                      </router-link>
                    </div>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group">
                    <label>Customer Name</label>
                    <input v-model.trim="form.customername" type="text" class="form-control" :disabled="readonly" />
                  </div>
                </div>
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <label>Mobile</label>
                    <InternationalPhoneInput
                      ref="quotationMobileInput"
                      v-model="form.mobile"
                      :disabled="readonly"
                      :submitted="phoneSubmitted"
                    />
                  </div>
                </div>
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <label>Quotation Date</label>
                    <input v-model="form.quotationdate" type="date" class="form-control" :disabled="readonly" />
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group">
                    <label>Valid Until</label>
                    <input v-model="form.validuntil" type="date" class="form-control" :disabled="readonly" />
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group">
                    <label>Purpose</label>
                    <select v-model="form.purpose" class="form-control" :disabled="readonly">
                      <option v-for="purpose in purposes" :key="purpose" :value="purpose">{{ purpose }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group">
                    <label>GSTIN</label>
                    <input v-model.trim="form.customergstin" type="text" maxlength="15" class="form-control" :disabled="readonly" />
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group quotation-check">
                    <label>&nbsp;</label>
                    <label class="checkboxs">
                      <input v-model="form.isinterstate" type="checkbox" :disabled="readonly" />
                      <span class="checkmarks"></span>
                      Interstate / IGST
                    </label>
                  </div>
                </div>
              </div>

              <div class="table-responsive quotation-table-wrap">
                <table class="table quotation-table">
                  <thead>
                    <tr>
                      <th style="width: 34%">Item Search</th>
                      <th class="text-end">Qty</th>
                      <th class="text-end">Rate</th>
                      <th class="text-end">Disc %</th>
                      <th class="text-end">Tax %</th>
                      <th>Tax Type</th>
                      <th class="text-end">Taxable</th>
                      <th class="text-end">Tax</th>
                      <th class="text-end">Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(line, index) in form.items" :key="line.uid">
                      <td>
                        <div class="lookup-box">
                          <input
                            v-model="line.searchText"
                            type="text"
                            autocomplete="off"
                            class="form-control"
                            placeholder="Type item name / code / barcode"
                            :disabled="readonly"
                            @focus="openItemIndex = index"
                            @input="openItemIndex = index"
                            @keydown.enter.prevent="selectFirstItem(index)"
                          />
                          <div v-if="openItemIndex === index && !readonly" class="lookup-results item-results">
                            <button
                              v-for="item in filteredItems(line.searchText)"
                              :key="item.itemid"
                              type="button"
                              class="lookup-result-item"
                              @mousedown.prevent="selectItem(item, index)"
                            >
                              <span class="lookup-result-title">{{ item.itemname }}</span>
                              <span class="lookup-result-meta">{{ item.itemcode || item.barcode || item.itemid }}</span>
                            </button>
                            <div v-if="line.searchText.trim() && !filteredItems(line.searchText).length" class="lookup-empty-state">
                              No item found
                            </div>
                          </div>
                        </div>
                      </td>
                      <td><input v-model.number="line.quantity" type="number" min="0.01" step="0.01" class="form-control text-end" :disabled="readonly || !line.itemid" /></td>
                      <td><input v-model.number="line.rate" type="number" min="0" step="0.01" class="form-control text-end" :disabled="readonly || !line.itemid" /></td>
                      <td><input v-model.number="line.discount" type="number" min="0" max="100" step="0.01" class="form-control text-end" :disabled="readonly || !line.itemid" /></td>
                      <td><input v-model.number="line.taxpercent" type="number" min="0" step="0.01" class="form-control text-end" :disabled="readonly || !line.itemid" /></td>
                      <td>
                        <select v-model="line.taxtype" class="form-control" :disabled="readonly || !line.itemid">
                          <option value="GST">GST</option>
                          <option value="SAC">SAC</option>
                          <option value="EXEMPT">EXEMPT</option>
                          <option value="NONGST">NONGST</option>
                        </select>
                      </td>
                      <td class="text-end">{{ line.itemid ? money(calcLine(line).taxablevalue) : "-" }}</td>
                      <td class="text-end">{{ line.itemid ? money(calcLine(line).taxamount) : "-" }}</td>
                      <td class="text-end">{{ line.itemid ? money(calcLine(line).amount) : "-" }}</td>
                      <td>
                        <button v-if="!readonly && form.items.length > 1" type="button" class="btn btn-sm btn-light" @click="removeLine(index)">
                          <i class="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="row mt-3">
                <div class="col-lg-7">
                  <div class="form-group">
                    <label>Remarks</label>
                    <textarea v-model.trim="form.remarks" class="form-control" rows="4" :disabled="readonly"></textarea>
                  </div>
                </div>
                <div class="col-lg-5">
                  <div class="total-order quotation-total">
                    <ul>
                      <li><h4>Sub Total</h4><h5>{{ money(totals.totalamount) }}</h5></li>
                      <li><h4>Tax</h4><h5>{{ money(totals.taxamount) }}</h5></li>
                      <li>
                        <h4>Discount</h4>
                        <h5><input v-model.number="form.discountamount" type="number" min="0" step="0.01" class="form-control text-end" :disabled="readonly" /></h5>
                      </li>
                      <li>
                        <h4>Round Off</h4>
                        <h5><input v-model.number="form.roundoff" type="number" step="0.01" class="form-control text-end" :disabled="readonly" /></h5>
                      </li>
                      <li class="total"><h4>Grand Total</h4><h5>{{ money(totals.netamount) }}</h5></li>
                    </ul>
                  </div>
                </div>
                <div class="col-lg-12">
                  <button v-if="!readonly" type="button" class="btn btn-submit me-2" :disabled="saving" @click="submitForm('DRAFT')">Update Draft</button>
                  <button v-if="!readonly" type="button" class="btn btn-submit me-2" :disabled="saving" @click="submitForm('SENT')">Update & Send</button>
                  <router-link to="quotationlist" class="btn btn-cancel">Back</router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import InternationalPhoneInput from "@/components/InternationalPhoneInput.vue";

const blankLine = () => ({
  uid: `${Date.now()}-${Math.random()}`,
  itemid: "",
  itemname: "",
  searchText: "",
  quantity: 1,
  rate: 0,
  discount: 0,
  taxpercent: 0,
  taxtype: "GST",
  istaxinclusive: false,
});

export default {
  name: "editquotation",
  components: { InternationalPhoneInput },
  data() {
    return {
      title: "Edit Quotation",
      title1: "Update draft quotation details",
      loading: true,
      saving: false,
      customers: [],
      itemsList: [],
      customerSearchQuery: "",
      showCustomerResults: false,
      phoneSubmitted: false,
      openItemIndex: null,
      purposes: ["GENERAL", "LOAN", "INSURANCE", "TENDER", "OTHER"],
      form: {
        status: "DRAFT",
        quotationdate: "",
        validuntil: "",
        customerid: "",
        customername: "",
        mobile: "",
        isinterstate: false,
        customergstin: "",
        placeofsupply: "",
        purpose: "GENERAL",
        discountamount: 0,
        roundoff: 0,
        remarks: "",
        items: [blankLine()],
      },
    };
  },
  computed: {
    readonly() {
      return this.form.status !== "DRAFT";
    },
    quotationid() {
      return this.$route.query.id;
    },
    customerSearchResults() {
      const term = this.customerSearchQuery.trim().toLowerCase();
      if (!term) return this.customers.slice(0, 20);
      return this.customers
        .filter((customer) => [customer.customername, customer.mobile, customer.customerid].some((value) => String(value || "").toLowerCase().includes(term)))
        .slice(0, 20);
    },
    activeItems() {
      return this.form.items.filter((line) => line.itemid);
    },
    totals() {
      const totalamount = this.activeItems.reduce((sum, line) => sum + this.calcLine(line).amount, 0);
      const taxamount = this.activeItems.reduce((sum, line) => sum + this.calcLine(line).taxamount, 0);
      const netamount = totalamount - Number(this.form.discountamount || 0) + Number(this.form.roundoff || 0);
      return {
        totalamount: Number(totalamount.toFixed(2)),
        taxamount: Number(taxamount.toFixed(2)),
        netamount: Number(netamount.toFixed(2)),
      };
    },
  },
  mounted() {
    this.loadPage();
  },
  methods: {
    async loadPage() {
      if (!this.quotationid) {
        await Swal.fire({ icon: "error", title: "Quotation Error", text: "Quotation id is missing." });
        this.$router.push({ name: "quotationlist" });
        return;
      }
      try {
        const [customersRes, itemsRes, quotationRes] = await Promise.all([
          axios.get("/v1/customers"),
          axios.get("/v1/items"),
          axios.get(`/v1/quotations/${this.quotationid}`),
        ]);
        this.customers = customersRes.data || [];
        this.itemsList = Array.isArray(itemsRes.data) ? itemsRes.data : itemsRes.data.items || [];
        this.hydrateForm(quotationRes.data);
      } catch (error) {
        this.showError(error, "Failed to load quotation");
      } finally {
        this.loading = false;
      }
    },
    hydrateForm(data) {
      const rows = (data.items || []).map((line) => ({
        uid: `${line.itemid}-${line.id}`,
        itemid: line.itemid,
        itemname: line.itemname || line.itemid,
        searchText: line.itemname || line.itemid,
        quantity: Number(line.quantity || 0),
        rate: Number(line.rate || 0),
        discount: Number(line.discount || 0),
        taxpercent: Number(line.taxpercent || 0),
        taxtype: line.taxtype || "GST",
        istaxinclusive: Boolean(line.istaxinclusive),
      }));
      this.form = {
        status: data.status,
        quotationdate: data.quotationdate || "",
        validuntil: data.validuntil || "",
        customerid: data.customerid || "",
        customername: data.customername || "",
        mobile: data.mobile || "",
        isinterstate: Boolean(data.isinterstate),
        customergstin: data.customergstin || "",
        placeofsupply: data.placeofsupply || "",
        purpose: data.purpose || "GENERAL",
        discountamount: Number(data.discountamount || 0),
        roundoff: Number(data.roundoff || 0),
        remarks: data.remarks || "",
        items: rows.concat(this.readonly ? [] : [blankLine()]),
      };
      this.customerSearchQuery = data.customername || "";
    },
    onCustomerLookupInput() {
      this.showCustomerResults = true;
      if (!this.customerSearchQuery.trim()) {
        this.form.customerid = "";
      }
    },
    selectCustomer(customer) {
      this.form.customerid = customer.customerid || "";
      this.form.customername = customer.customername || "";
      this.form.mobile = customer.mobile || "";
      this.form.customergstin = customer.gstno || "";
      this.customerSearchQuery = customer.customername || "";
      this.showCustomerResults = false;
    },
    filteredItems(searchText) {
      const term = (searchText || "").trim().toLowerCase();
      if (!term) return [];
      return this.itemsList
        .filter((item) => [item.itemname, item.itemcode, item.barcode, item.itemid].some((value) => String(value || "").toLowerCase().includes(term)))
        .slice(0, 30);
    },
    selectFirstItem(index) {
      const first = this.filteredItems(this.form.items[index].searchText)[0];
      if (first) this.selectItem(first, index);
    },
    selectItem(item, index) {
      const line = this.form.items[index];
      line.itemid = item.itemid;
      line.itemname = item.itemname;
      line.searchText = item.itemname;
      line.rate = Number(item.unitprice || item.mrp || 0);
      line.taxpercent = Number(item.taxpercent || 0);
      line.taxtype = item.taxtype || "GST";
      line.istaxinclusive = Boolean(item.istaxinclusive);
      this.openItemIndex = null;
      if (index === this.form.items.length - 1) {
        this.form.items.push(blankLine());
      }
    },
    removeLine(index) {
      this.form.items.splice(index, 1);
      if (!this.form.items.length) this.form.items.push(blankLine());
    },
    calcLine(line) {
      const quantity = Number(line.quantity || 0);
      const rate = Number(line.rate || 0);
      const discount = Math.min(Math.max(Number(line.discount || 0), 0), 100);
      const taxPercent = ["EXEMPT", "NONGST"].includes(line.taxtype) ? 0 : Number(line.taxpercent || 0);
      const gross = quantity * rate;
      const discounted = gross - gross * discount / 100;
      const taxable = line.istaxinclusive && taxPercent > 0 ? discounted / (1 + taxPercent / 100) : discounted;
      const taxamount = taxable * taxPercent / 100;
      const amount = line.istaxinclusive ? discounted : taxable + taxamount;
      return {
        taxablevalue: Number(taxable.toFixed(2)),
        taxamount: Number(taxamount.toFixed(2)),
        amount: Number(amount.toFixed(2)),
      };
    },
    buildPayload() {
      return {
        ...this.form,
        status: "DRAFT",
        totalamount: this.totals.totalamount,
        taxamount: this.totals.taxamount,
        netamount: this.totals.netamount,
        validuntil: this.form.validuntil || null,
        customergstin: this.form.customergstin || null,
        placeofsupply: this.form.placeofsupply || null,
        items: this.activeItems.map((line, index) => {
          const calc = this.calcLine(line);
          const taxHalf = this.form.isinterstate ? 0 : calc.taxamount / 2;
          return {
            itemid: line.itemid,
            quantity: Number(line.quantity || 0),
            rate: Number(line.rate || 0),
            discount: Number(line.discount || 0),
            taxpercent: ["EXEMPT", "NONGST"].includes(line.taxtype) ? 0 : Number(line.taxpercent || 0),
            taxablevalue: calc.taxablevalue,
            cgst: Number(taxHalf.toFixed(2)),
            sgst: Number(taxHalf.toFixed(2)),
            igst: this.form.isinterstate ? calc.taxamount : 0,
            taxtype: line.taxtype,
            istaxinclusive: Boolean(line.istaxinclusive),
            taxamount: calc.taxamount,
            amount: calc.amount,
            sortorder: index + 1,
          };
        }),
      };
    },
    async submitForm(statusValue) {
      if (!this.form.customername) {
        Swal.fire({ icon: "warning", title: "Customer required", text: "Customer name is required." });
        return;
      }
      this.phoneSubmitted = true;
      if (!this.$refs.quotationMobileInput?.validate()) {
        Swal.fire({ icon: "warning", title: "Invalid mobile number" });
        return;
      }
      if (!this.activeItems.length) {
        Swal.fire({ icon: "warning", title: "Item required", text: "Select at least one item." });
        return;
      }
      this.saving = true;
      try {
        await axios.put(`/v1/quotations/${this.quotationid}`, this.buildPayload());
        if (statusValue === "SENT") {
          await axios.patch(`/v1/quotations/${this.quotationid}/status`, { status: "SENT" });
        }
        await Swal.fire({ icon: "success", title: "Quotation Updated", timer: 1400, showConfirmButton: false });
        this.$router.push({ name: "quotationlist" });
      } catch (error) {
        this.showError(error, "Failed to update quotation");
      } finally {
        this.saving = false;
      }
    },
    showError(error, fallback) {
      Swal.fire({
        icon: "error",
        title: "Quotation Error",
        text: error?.response?.data?.detail || fallback,
      });
    },
    money(value) {
      return Number(value || 0).toFixed(2);
    },
  },
};
</script>

<style scoped>
.lookup-control-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.lookup-box {
  position: relative;
  flex: 1 1 auto;
}
.lookup-results {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  z-index: 40;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #d7e2f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.14);
}
.lookup-result-item {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 9px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.lookup-result-item:hover {
  background: #f3f8ff;
}
.lookup-result-title {
  color: #111827;
  font-weight: 600;
}
.lookup-result-meta,
.lookup-empty-state {
  color: #6b7280;
  font-size: 0.82rem;
}
.lookup-empty-state {
  padding: 12px;
}
.inline-add-btn {
  min-height: 38px;
  padding: 0 12px;
  border: 1px solid #bfd6f7;
  border-radius: 8px;
  background: #eff6ff;
  color: #1d4ed8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}
.quotation-table-wrap {
  overflow: visible;
}
.quotation-table td {
  vertical-align: top;
}
.item-results {
  min-width: 300px;
}
.quotation-check {
  padding-top: 1px;
}
.quotation-total input {
  height: 32px;
  min-width: 120px;
}
td .form-control {
  min-width: 84px;
}
</style>
